/**
 * Booking price, discounts and deposit.
 *
 * This is the single most duplicated area of the legacy codebase: the same
 * cumulative-discount arithmetic appeared in `updateBookingDetailsUI`,
 * `submitPatientBooking`, `_recalcBMPrice`, `updateBookingModalPrices`,
 * `updateAdminBookingPrice` and `saveManualBooking` — six copies that had
 * already drifted apart. This module is the one implementation, and it matches
 * the behaviour the live system actually shipped:
 *
 *   final = base
 *         × (1 − day/100)
 *         × (1 − night/100)
 *         × (1 − admin/100)
 *         × (1 − voucher/100)
 *
 * applied *sequentially* (each percentage bites into what is left, not into the
 * original), then rounded once at the end.
 *
 * Deposit is derived from the clinic, matching what the backend recomputes in
 * POST /api/bookings/create — the client and server must agree or the patient
 * is shown a figure the server will overwrite.
 */

import { formatMoney } from './locale';

export const BOOKING_TYPES = { EXAM: 'exam', FOLLOWUP: 'followup' };

/** Fallbacks the legacy code used whenever a price field was missing. */
export const PRICE_FALLBACKS = { exam: 300, followup: 150 };

const num = (value, fallback = 0) => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampPercent = (value) => Math.min(100, Math.max(0, num(value, 0)));

/**
 * Base consultation price.
 *
 * Clinic-level pricing wins when present (the clinic editor is the source of
 * truth the backend reads); the doctor-level price is the fallback for doctors
 * who never configured per-clinic prices.
 */
export function resolveBasePrice({ doctor, clinic, bookingType = BOOKING_TYPES.EXAM }) {
  const isFollowup = bookingType === BOOKING_TYPES.FOLLOWUP;

  if (clinic) {
    const clinicPrice = isFollowup ? clinic.followup_price : clinic.examination_price;
    if (clinicPrice !== null && clinicPrice !== undefined && clinicPrice !== '') {
      return num(clinicPrice, 0);
    }
  }

  if (doctor) {
    const doctorPrice = isFollowup
      ? doctor.price_followup ?? doctor.priceFollowup
      : doctor.price_exam ?? doctor.priceExam;
    if (doctorPrice !== null && doctorPrice !== undefined && doctorPrice !== '') {
      return num(doctorPrice, 0);
    }
    // A doctor with no follow-up price falls back to the exam price, as before.
    if (isFollowup) {
      const examPrice = doctor.price_exam ?? doctor.priceExam;
      if (examPrice !== null && examPrice !== undefined && examPrice !== '') {
        return num(examPrice, 0);
      }
    }
  }

  return isFollowup ? PRICE_FALLBACKS.followup : PRICE_FALLBACKS.exam;
}

/**
 * Apply the discount chain.
 *
 * @param {number} basePrice
 * @param {object} discounts
 * @param {number} [discounts.day]      Day/date offer percentage.
 * @param {number} [discounts.night]    Night or extra-hours percentage.
 * @param {number} [discounts.admin]    Admin override percentage.
 * @param {number} [discounts.voucher]  Voucher code percentage.
 * @returns {{
 *   basePrice: number, finalPrice: number, discountAmount: number,
 *   effectivePercent: number, autoDiscountPercent: number,
 *   breakdown: {day: number, night: number, admin: number, voucher: number},
 *   amounts: {day: number, night: number, admin: number, voucher: number}
 * }}
 *
 * `breakdown` holds the percentages; `amounts` what each stage took off in
 * pounds, measured between the rounded running totals so the lines a receipt
 * prints always add up to `discountAmount`.
 */
export function calculateBookingPrice(basePrice, discounts = {}) {
  const base = Math.max(0, num(basePrice, 0));
  const day = clampPercent(discounts.day);
  const night = clampPercent(discounts.night);
  const admin = clampPercent(discounts.admin);
  const voucher = clampPercent(discounts.voucher);

  const afterDay = base * (1 - day / 100);
  const afterNight = afterDay * (1 - night / 100);
  const afterAdmin = afterNight * (1 - admin / 100);
  const afterVoucher = afterAdmin * (1 - voucher / 100);

  const finalPrice = Math.round(afterVoucher);
  const discountAmount = base - finalPrice;
  const [r1, r2, r3] = [afterDay, afterNight, afterAdmin].map(Math.round);

  // "auto" = the part the patient did not have to type a code for (day + night).
  const autoDiscountPercent = base > 0 ? Math.round(((base - afterNight) / base) * 100) : 0;
  const effectivePercent = base > 0 ? Math.round((discountAmount / base) * 100) : 0;

  return {
    basePrice: base,
    finalPrice,
    discountAmount,
    effectivePercent,
    autoDiscountPercent,
    breakdown: { day, night, admin, voucher },
    amounts: { day: base - r1, night: r1 - r2, admin: r2 - r3, voucher: r3 - finalPrice },
  };
}

/**
 * Deposit for a booking, mirroring the backend's derivation.
 *
 * An explicit `*_deposit_amount` on the clinic wins; otherwise the percentage is
 * applied to the *final* price. When the clinic does not require a deposit the
 * result is zeroed out, so callers never have to special-case it.
 */
export function calculateDeposit({ clinic, bookingType = BOOKING_TYPES.EXAM, finalPrice = 0 }) {
  const required = clinic?.requireDeposit === true || clinic?.requireDeposit === 'true';
  if (!clinic || !required) {
    return { required: false, percent: 0, amount: 0, instaPayAccount: '' };
  }

  const isFollowup = bookingType === BOOKING_TYPES.FOLLOWUP;
  const percentRaw = isFollowup
    ? clinic.followup_deposit_percentage
    : clinic.examination_deposit_percentage;
  const amountRaw = isFollowup
    ? clinic.followup_deposit_amount
    : clinic.examination_deposit_amount;

  const percent = clampPercent(percentRaw);

  let amount;
  if (amountRaw !== null && amountRaw !== undefined && amountRaw !== '') {
    amount = Math.round(num(amountRaw, 0));
  } else {
    amount = Math.round((percent / 100) * num(finalPrice, 0));
  }

  return {
    required: true,
    percent,
    amount,
    instaPayAccount: clinic.instapay_account || clinic.instaPayAccount || '',
  };
}

/**
 * Clinic extras a patient ticked, priced.
 *
 * Accepts whichever shape the caller has: the raw `extra_services` rows the
 * clinic endpoint returns (`service_name_ar` / `service_price`) or the frozen
 * `{ id, name, price }` copies stored on a booking. Both spellings exist in
 * live data, and a screen that reads only one of them silently prices a real
 * service at zero.
 *
 * @returns {{ services: Array<{id:*, name:string, price:number}>, total:number }}
 */
export function priceServices(selected = []) {
  const services = (Array.isArray(selected) ? selected : [])
    .filter(Boolean)
    .map((entry) => ({
      id: entry.id,
      name: entry.name || entry.service_name_ar || "",
      price: Math.max(0, Math.round(num(entry.price ?? entry.service_price, 0))),
    }));

  return {
    services,
    total: services.reduce((sum, service) => sum + service.price, 0),
  };
}

/**
 * One call that produces everything the booking UI and the create-booking
 * payload need. Keeping this together is what stops the two from drifting.
 *
 * Extras sit *outside* the discount chain and are added afterwards. Two
 * reasons: a day offer is on the doctor’s fee, not on a dressing or a lab
 * test someone else performs; and keeping them out means `finalPrice` can
 * never climb above `originalPrice`, which is the invariant the booking
 * integrity check enforces. `finalPrice` therefore stays the consultation
 * figure it has always been, and `grandTotal` is what the patient pays.
 */
export function quoteBooking({
  doctor,
  clinic,
  bookingType = BOOKING_TYPES.EXAM,
  dayDiscount = 0,
  nightDiscount = 0,
  adminDiscount = 0,
  voucherDiscount = 0,
  selectedServices = [],
}) {
  const basePrice = resolveBasePrice({ doctor, clinic, bookingType });
  const price = calculateBookingPrice(basePrice, {
    day: dayDiscount,
    night: nightDiscount,
    admin: adminDiscount,
    voucher: voucherDiscount,
  });

  // The deposit holds the appointment, so it is a percentage of the visit —
  // not of optional extras the patient has not received yet. This matches
  // what the backend stores.
  const deposit = calculateDeposit({ clinic, bookingType, finalPrice: price.finalPrice });

  const extras = priceServices(selectedServices);

  return {
    ...price,
    bookingType,
    deposit,
    services: extras.services,
    servicesTotal: extras.total,
    grandTotal: price.finalPrice + extras.total,
    grandOriginal: price.basePrice + extras.total,
  };
}

/**
 * What a doctor actually charges, across their clinics.
 *
 * `resolveBasePrice` takes the clinic's price first and only falls back to
 * DoctorProfile, so any screen that reads `doctor.price_exam` on its own is
 * showing the profile column — which is 500/200 by schema default and, for most
 * doctors, a number nobody set and no patient is ever quoted.
 *
 * A doctor working in two clinics can charge two different prices, so this
 * returns the range rather than pretending there is one figure.
 *
 * @returns {{min: number, max: number, varies: boolean, fromClinic: boolean}}
 *          `fromClinic` is false when the doctor has no clinic and the profile
 *          fallback is all there is — the caller can then say so instead of
 *          presenting it as a real price.
 */
export function doctorPriceRange({ doctor, bookingType = BOOKING_TYPES.EXAM }) {
  const clinics = Array.isArray(doctor?.clinics) ? doctor.clinics : [];

  if (clinics.length === 0) {
    const price = resolveBasePrice({ doctor, clinic: null, bookingType });
    return { min: price, max: price, varies: false, fromClinic: false };
  }

  const prices = clinics.map((clinic) => resolveBasePrice({ doctor, clinic, bookingType }));
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    varies: Math.min(...prices) !== Math.max(...prices),
    fromClinic: true,
  };
}

/** The range as one string: "256 ج.م" or "من 256 ج.م" when clinics differ. */
export function formatPriceRange(range) {
  if (!range) return '—';
  return range.varies ? `من ${formatCurrency(range.min)}` : formatCurrency(range.min);
}

/** Sum of `finalPrice` over completed bookings — the revenue figure the dashboard shows. */
export function sumCompletedRevenue(bookings = []) {
  return bookings
    .filter((b) => b.status === 'completed')
    .reduce((total, b) => total + num(b.finalPrice, 0), 0);
}

/**
 * Format a figure the way the app displays money throughout: in the
 * institution's currency (domain/locale.js — Egyptian pounds by default).
 */
export function formatCurrency(amount) {
  return formatMoney(num(amount, 0));
}
